import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import { useSwipeable } from "react-swipeable";
import clsx from "clsx";

import {
  ARROW_DOWN,
  ARROW_LEFT,
  ARROW_RIGHT,
  ARROW_UP,
} from "../../../../../constants/key";
import {
  DEFAULT_PAGE,
  SLIDER_DURATION_MS,
  CONTROLS_VISIBLE_MS,
} from "../constants";
import { ReactComponent as ArrowIcon } from "../../../../../assets/svg/arrow_right_single.svg";

import styles from "./Controls.module.scss";

type Props = {
  currentPage: number;
  maxPage: number;
  onChangePage: (page: number) => void;
};

const Controls: FC<Props> = ({ currentPage, maxPage, onChangePage }) => {
  const [isControlsVisible, setIsControlsVisible] = useState(false);
  const controlsVisibilityTimer = useRef<ReturnType<typeof setTimeout>>();
  const pageChangeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isPrevDisabled = currentPage <= DEFAULT_PAGE;
  const isNextDisabled = currentPage >= maxPage;

  const changePageWithLock = (changePage: () => void) => {
    if (pageChangeTimer.current) return;

    changePage();

    pageChangeTimer.current = setTimeout(() => {
      pageChangeTimer.current = null;
    }, SLIDER_DURATION_MS);
  };

  const handlePrevPage = useCallback(() => {
    if (isPrevDisabled) return;

    changePageWithLock(() => {
      onChangePage(currentPage - 1);
    });
  }, [currentPage, isPrevDisabled, onChangePage]);

  const handleNextPage = useCallback(() => {
    if (isNextDisabled) return;

    changePageWithLock(() => {
      onChangePage(currentPage + 1);
    });
  }, [currentPage, isNextDisabled, onChangePage]);

  const handleShowControls = () => {
    setIsControlsVisible(true);

    if (controlsVisibilityTimer.current) {
      clearTimeout(controlsVisibilityTimer.current);
    }

    controlsVisibilityTimer.current = setTimeout(() => {
      setIsControlsVisible(false);
    }, CONTROLS_VISIBLE_MS);
  };

  useEffect(() => {
    return () => {
      if (controlsVisibilityTimer.current) {
        clearTimeout(controlsVisibilityTimer.current);
      }
      if (pageChangeTimer.current) {
        clearTimeout(pageChangeTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (/input|textarea/i.test((e.target as HTMLElement).tagName)) return;

      if (e.key === ARROW_DOWN || e.key === ARROW_RIGHT) {
        handleNextPage();
      }

      if (e.key === ARROW_UP || e.key === ARROW_LEFT) {
        handlePrevPage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleNextPage, handlePrevPage]);

  const swipeHandlers = useSwipeable({
    onSwipedUp: e => {
      e.event.stopPropagation();
      handleNextPage();
    },
    onSwipedDown: e => {
      e.event.stopPropagation();
      handlePrevPage();
    },
    delta: 60,
    swipeDuration: SLIDER_DURATION_MS,
  });

  return (
    <div
      className={clsx(styles.controls, isControlsVisible && styles.isVisible)}
      onClick={handleShowControls}
      {...swipeHandlers}
    >
      <button
        type="button"
        className={clsx(styles.paginationButton, styles.prev)}
        disabled={isPrevDisabled}
        onClick={e => {
          e.stopPropagation();
          handlePrevPage();
        }}
      >
        <ArrowIcon />
      </button>
      <button
        type="button"
        className={clsx(styles.paginationButton, styles.next)}
        disabled={isNextDisabled}
        onClick={e => {
          e.stopPropagation();
          handleNextPage();
        }}
      >
        <ArrowIcon />
      </button>
    </div>
  );
};

export default Controls;
